import React, { useState, useEffect } from 'react';
import { FolderOpen, Trash2, Edit2, Calendar, FileText, Sparkles, Search, Plus } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { templateApi, Template } from '../services/templateApi';
import toast from 'react-hot-toast';

const TemplateManager: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { userId, isLoaded } = useAuth();
  const navigate = useNavigate();

  // Rename Dialog State
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [templateToRename, setTemplateToRename] = useState<Template | null>(null);
  const [newTemplateName, setNewTemplateName] = useState('');

  // Delete Dialog State
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null);

  useEffect(() => {
    const loadTemplates = async () => {
      if (!userId) return;
      setIsLoading(true);
      try {
        const data = await templateApi.getTemplates(userId);
        setTemplates(data);
      } catch (error) {
        console.error('Error loading templates:', error);
        toast.error('Failed to load templates');
      } finally {
        setIsLoading(false);
      }
    };

    if (isLoaded && userId) {
      loadTemplates();
    }
  }, [isLoaded, userId]);

  const handleDeleteTemplate = async () => {
    if (!templateToDelete?._id || !userId) return;

    try {
      await templateApi.deleteTemplate(templateToDelete._id, userId);
      setTemplates(prev => prev.filter(t => t._id !== templateToDelete._id));
      toast.success('Template deleted');
      setIsDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleUpdateTemplate = async () => {
    if (!templateToRename?._id || !newTemplateName.trim() || !userId) {
      toast.error('Template name is required');
      return;
    }

    try {
      const updated = await templateApi.updateTemplate(templateToRename._id, {
        name: newTemplateName,
        subject: templateToRename.subject,
        htmlContent: templateToRename.htmlContent,
        plainText: templateToRename.plainText
      }, userId);

      setTemplates(prev => prev.map(t => (t._id === updated._id ? updated : t)));
      toast.success('Template renamed');
      setIsRenameDialogOpen(false);
      setTemplateToRename(null);
      setNewTemplateName('');
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error('Failed to update template');
    }
  };

  const handleUseTemplate = (template: Template) => {
    navigate('/app', { state: { selectedTemplate: template } });
  };

  const openRenameDialog = (template: Template) => {
    setTemplateToRename(template);
    setNewTemplateName(template.name);
    setIsRenameDialogOpen(true);
  };

  const openDeleteDialog = (template: Template) => {
    setTemplateToDelete(template);
    setIsDeleteDialogOpen(true);
  };

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <FolderOpen className="w-8 h-8 mr-3 text-blue-600 dark:text-blue-400" />
            Template Library
          </h2>
          <p className="mt-1 text-gray-500 dark:text-gray-400">Manage and reuse your email designs</p>
        </div>
        <div className="w-full sm:w-auto flex gap-3">
          <div className="relative flex-grow sm:flex-grow-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search templates..."
              className="pl-9 pr-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 w-full sm:w-64 transition-colors"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={() => navigate('/app')}
            className="btn-primary flex items-center whitespace-nowrap"
          >
            <Plus className="w-4 h-4 mr-2" />
            New
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((n) => (
            <div key={n} className="h-48 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse"></div>
          ))}
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-800">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 dark:bg-blue-900/20 mb-4">
            <FileText className="w-8 h-8 text-blue-500 dark:text-blue-400" />
          </div>
          <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">No templates found</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
            {searchTerm ? "Try adjusting your search terms" : "Create your first email template in the Compose tab to get started."}
          </p>
          {!searchTerm && (
            <button onClick={() => navigate('/app')} className="btn-primary">
              Go to Compose
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => (
            <div
              key={template._id}
              className="group bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-lg hover:border-blue-200 dark:hover:border-blue-800 transition-all duration-300 flex flex-col overflow-hidden"
            >
              {/* Preview Header */}
              <div className="p-5 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate pr-2" title={template.name}>
                    {template.name}
                  </h3>
                  <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); openRenameDialog(template); }}
                      className="p-1.5 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 rounded-md hover:bg-white dark:hover:bg-gray-700 transition"
                      title="Rename"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); openDeleteDialog(template); }}
                      className="p-1.5 text-gray-500 hover:text-red-600 dark:hover:text-red-400 rounded-md hover:bg-white dark:hover:bg-gray-700 transition"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                  <Calendar className="w-3 h-3 mr-1" />
                  {template.createdAt ? new Date(template.createdAt).toLocaleDateString() : 'N/A'}
                </div>
              </div>

              {/* Content Preview */}
              <div className="p-5 flex-grow cursor-pointer" onClick={() => handleUseTemplate(template)}>
                <div className="space-y-3">
                  <div className="text-sm">
                    <span className="text-gray-900 dark:text-gray-300 font-medium">Subject: </span>
                    <span className="text-gray-600 dark:text-gray-400 clamp-1 ml-1">{template.subject}</span>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-xs text-gray-500 dark:text-gray-400 h-24 overflow-hidden relative">
                    {template.plainText || "No content preview available"}
                    <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-gray-50 dark:from-gray-800 to-transparent"></div>
                  </div>
                </div>
              </div>

              {/* Actions Footer */}
              <div className="p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/30 dark:bg-gray-800/30">
                <button
                  onClick={() => handleUseTemplate(template)}
                  className="w-full btn-primary flex justify-center items-center py-2 text-sm"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Use Template
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Rename Dialog */}
      {isRenameDialogOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-scale-in">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <Edit2 className="w-5 h-5 mr-2 text-blue-600" />
                Rename Template
              </h3>
            </div>
            <div className="p-6">
              <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
                Enter a new name for your template.
              </p>
              <input
                type="text"
                className="input-field"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                autoFocus
              />
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setIsRenameDialogOpen(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateTemplate}
                  className="btn-primary"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      {isDeleteDialogOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden animate-scale-in">
            <div className="p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete Template?</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6 text-sm">
                Are you sure you want to delete <span className="font-semibold text-gray-900 dark:text-white">"{templateToDelete?.name}"</span>? This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setIsDeleteDialogOpen(false)}
                  className="btn-secondary w-full"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteTemplate}
                  className="btn-primary bg-red-600 hover:bg-red-700 w-full focus:ring-red-500"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateManager;
